import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { generateTemporaryAccess, getTemporaryAccessPin, type IdCompanyDto } from '@/services/ws-companies/src/main/resources/rest-api';
import { fromDateTimeType } from '@/js/libs/api-helpers';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ModalStateOpen } from '@/js/libs/hooks/useModalState';
import { useSubscribe } from '@/js/libs/observable';
import { Button, Loader, Modal, Notification } from '@/js/libs/ui/src/V2';
import { NotActive, useRequest, type Data } from '@/js/libs/use-request';

import { CopyTooltip } from 'src/components/CopyTooltip';
import { computeTimeLeft, type TimeLeft } from './helpers';
import { PinContainer } from './styled';

type GrantAccessModalProps = {
  state: ModalStateOpen;
  companyId: IdCompanyDto;
};

export const GrantAccessModal = memo(function GrantAccessModal({ state, companyId }: GrantAccessModalProps) {
  const [t] = useTranslation(['components', 'common']);

  const generateRequest = useRequest({
    apiMethod: generateTemporaryAccess,
    params: { companyId },
  });
  const isGenerated = useSubscribe(generateRequest.data$, (response) => Boolean(response));

  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ min: 0, sec: 0 });
  const intervalIdRef = useRef<number>();

  const stopCountdown = useCallbackRef(() => window.clearInterval(intervalIdRef.current));

  const startCountdown = useCallbackRef((response: Data<typeof getTemporaryAccessPin>) => {
    const expiresAtMs = fromDateTimeType(response?.data?.expiresAt)?.valueOf() ?? null;
    if (!expiresAtMs) return;
    stopCountdown();

    const tick = () => {
      const now = Date.now();
      setTimeLeft(computeTimeLeft(expiresAtMs, now));
      if (now >= expiresAtMs) stopCountdown();
    };

    tick();
    intervalIdRef.current = window.setInterval(tick, 1000);
  });

  const getPinRequest = useRequest({
    apiMethod: getTemporaryAccessPin,
    params: isGenerated ? undefined : NotActive,
    onSuccess: startCountdown,
  });
  const pin = useSubscribe(getPinRequest.data$, (response) => response?.data ?? null);

  const generatePending = useSubscribe(generateRequest.pending$);
  const getPinPending = useSubscribe(getPinRequest.pending$);
  const pending = generatePending || getPinPending;

  useEffect(() => stopCountdown, [stopCountdown]);

  const { min, sec } = timeLeft;

  return (
    <Modal state={state} title={t('components:GRANT_ADMIN_ACCESS')}>
      <Modal.Body>
        {pending && !pin && <Loader cover />}
        {pin && (
          <>
            <Notification body={t('components:GRANT_ADMIN_ACCESS_PROVIDE_HINT')} uncloseable spacer fullWidth />
            <PinContainer>
              <p>{t('components:GRANT_ADMIN_ACCESS_CODE_VALID', { min, sec })}</p>
              <CopyTooltip value={pin.pin} />
            </PinContainer>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={state.close}>{t('common:CONTINUE')}</Button>
      </Modal.Footer>
    </Modal>
  );
});
